import { formatCZK, plur } from "@/lib/format";

// Osa „Dnes“ a dlaždice mobilního přehledu vedení (196). Z GET /dashboard/today
// složí řádky osy pro vybraný filtr: nad čarou „Teď“ to, co teprve přijde nebo visí
// (úkoly, splatnosti, nezapsaný deník), pod ní, co se už stalo (nejnovější nahoře).
// Docházka: kdo je v práci = přišpendlený pod „Teď“, kdo odešel = řádek v čase
// odchodu. Ve „Vše“ obojí jako jeden souhrnný řádek, jednotlivě až ve filtru.

export const FILTERS = [["vse", "Vše"], ["ukoly", "Úkoly"], ["dochazka", "Docházka"], ["doklady", "Doklady"], ["objednavky", "Objednávky"], ["denik", "Deník"]];

const EMPTY = {
  vse: "Zatím se dnes nic nestalo",
  ukoly: "Dnes zatím nic hotového",
  dochazka: "Dnes zatím nikdo nepřišel",
  doklady: "Dnes zatím žádný doklad",
  objednavky: "Dnes nic nedorazí ani se nepřebírá",
  denik: "Dnes zatím žádný zápis",
};

const TZ = "Europe/Prague";
let HM = null, YMD = null;
try {
  HM = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: TZ });
  YMD = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: TZ });
} catch { /* starý prohlížeč bez časových pásem — počítá se v čase telefonu */ }

const parts = (fmt, d) => Object.fromEntries(fmt.formatToParts(d).map((x) => [x.type, x.value]));

// minuta dne v Praze (0–1439) z ISO času
export function pragueMin(iso) {
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  if (!HM) return d.getHours() * 60 + d.getMinutes();
  const p = parts(HM, d);
  return (Number(p.hour) % 24) * 60 + Number(p.minute);
}

// „2026-10-12“ v Praze
export function pragueDate(iso) {
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (!YMD) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const p = parts(YMD, d);
  return `${p.year}-${p.month}-${p.day}`;
}

export const minToHm = (m) => `${Math.floor(m / 60)}:${String(Math.floor(m % 60)).padStart(2, "0")}`;
const hmToMin = (s) => {
  const [h, m] = String(s || "").split(":").map(Number);
  return Number.isFinite(h) ? h * 60 + (Number.isFinite(m) ? m : 0) : null;
};
const ymdUtc = (s) => { const [y, m, d] = String(s || "").slice(0, 10).split("-").map(Number); return Date.UTC(y, (m || 1) - 1, d || 1); };
export const daysBetween = (from, to) => Math.round((ymdUtc(to) - ymdUtc(from)) / 86400000);

// „včera“ / „7. 10.“
export function dayLabel(date, today) {
  const n = daysBetween(date, today);
  if (n === 0) return "dnes";
  if (n === 1) return "včera";
  const [, m, d] = String(date).slice(0, 10).split("-").map(Number);
  return `${d}. ${m}.`;
}

export const lidi = (n) => (n === 1 ? "člověk" : n >= 2 && n <= 4 ? "lidé" : "lidí");
export const decH = (h) => `${String(Math.round((Number(h) || 0) * 10) / 10).replace(".", ",")} h`;
const first = (name) => String(name || "").split(" ")[0] || "—";
const uniq = (a) => [...new Set(a)];
const money = (v, sign = "−") => `${sign}${formatCZK(Math.abs(Number(v) || 0))}`;
const join = (...a) => a.filter(Boolean).join(" · ");
const ts = (iso) => { const t = Date.parse(iso || ""); return Number.isNaN(t) ? 0 : t; };

const APPROVAL = {
  approved: ["schváleno", "ok"],
  pending: ["ke schválení", "brand"],
  draft: ["k vyplnění", "brand"],
};

function taskSub(t) {
  const who = (t.assignees || []).filter((x) => x && x !== "—");
  return join(t.project_name || "Bez zakázky", who.length ? who.join(", ") : "úkol");
}

// ---- události (bez docházky) ----
function events(d, nowMin) {
  const today = d.today;
  const ev = [];
  const T = d.tasks || {};
  (T.today || []).forEach((t) => {
    const m = t.time_from ? hmToMin(t.time_from) : null;
    const late = m != null && m <= nowMin;
    ev.push({ key: `t-${t.id}`, kind: "ukoly", up: true, late, m: m != null ? m : nowMin + 0.4, t: m != null ? minToHm(m) : "dnes",
      title: t.title || "Úkol", sub: taskSub(t), pay: late ? "po termínu" : "", payTone: "danger", act: { type: "task", id: t.id } });
  });
  (T.overdue || []).forEach((t) => {
    ev.push({ key: `to-${t.id}`, kind: "ukoly", up: true, old: true, late: true, m: -(t.days_overdue || 1) * 1440,
      t: t.deadline ? dayLabel(t.deadline, today) : "dříve", title: t.title || "Úkol", sub: taskSub(t),
      pay: "po termínu", payTone: "danger", act: { type: "task", id: t.id } });
  });
  // 328: úkoly bez termínu nebo na později v Dnes nejsou — ve filtru Úkoly na ně odkaz, ať nevypadá, že nic není
  if ((T.laterCount || 0) > 0) {
    const n = T.laterCount;
    ev.push({ key: "t-later", kind: "ukoly", up: true, only: "ukoly", m: 1e6, t: "dál", title: `Další úkoly · ${n}`,
      sub: "bez termínu nebo na později", pay: "otevřít", payTone: "brand", act: { type: "tasks" } });
  }
  (T.done || []).forEach((t) => {
    const m = pragueMin(t.done_at);
    ev.push({ key: `td-${t.id}`, kind: "ukoly", m: m ?? 0, t: t.done_time || (m != null ? minToHm(m) : ""),
      title: `Hotovo · ${t.title || "úkol"}`, sub: join(t.project_name, t.done_by), act: { type: "task", id: t.id } });
  });
  if (d.money) {
    (d.due || []).forEach((x) => {
      const over = (x.days_overdue || 0) > 0;
      ev.push({ key: `due-${x.id}`, kind: "doklady", up: true, old: over, m: over ? -x.days_overdue * 1440 : nowMin + 0.5,
        t: over ? dayLabel(x.due_date, today) : "dnes", title: x.title, sub: join("Faktura", x.project_name),
        amt: money(x.amount), pay: over ? "po splatnosti" : "splatné dnes", payTone: "danger", act: { type: "doc" } });
    });
  }
  (d.docs || []).forEach((x) => {
    const m = pragueMin(x.at);
    const pay = !d.money ? "" : x.credit_note ? "dobropis" : String(x.status || "").toLowerCase();
    ev.push({ key: `doc-${x.kind}-${x.id}`, kind: "doklady", m: m ?? 0, t: x.time || "",
      title: `${x.kind === "uctenka" ? "Účtenka" : "Faktura"} · ${x.title}`, sub: join(x.project_name, x.by),
      amt: d.money && x.amount != null ? money(x.amount, x.credit_note ? "+" : "−") : "",
      pay, payTone: pay === "nezaplaceno" ? "brand" : "muted", act: { type: "doc" } });
  });
  // 327: objednávky — dodávka dnes / nedorazilo / k vyzvednutí (nad čarou), dnes převzato (pod ní)
  (d.orders || []).forEach((o) => {
    const where = join(o.title, o.project_name);
    const amt = d.money && o.amount != null ? formatCZK(o.amount) : "";
    const act = { type: "order", id: o.id };
    if (o.status === "received") {
      const m = pragueMin(o.received_at);
      const bad = o.result === "partial" ? "něco chybí" : o.result === "damaged" ? "poškozené" : "";
      ev.push({ key: `or-${o.id}`, kind: "objednavky", m: m ?? 0, t: o.time || (m != null ? minToHm(m) : ""),
        title: `Převzato · ${o.supplier}`, sub: join(where, o.received_by), amt,
        pay: bad || "v pořádku", payTone: bad ? "danger" : "ok", act });
      return;
    }
    const late = (o.days_late || 0) > 0;
    const pickup = o.status === "ready";
    const who = pickup ? o.branch : o.receiver ? `přebírá ${o.receiver}` : null;
    ev.push({ key: `or-${o.id}`, kind: "objednavky", up: true, late, m: late ? -o.days_late * 1440 : nowMin + 0.3,
      t: late ? dayLabel(o.due_date, today) : "dnes", title: `${pickup ? "K vyzvednutí" : "Dodávka"} · ${o.supplier}`,
      sub: join(where, who), amt,
      pay: late ? "nedorazilo" : pickup ? "na pobočce" : o.status === "shipped" ? "na cestě" : "dorazí dnes",
      payTone: late ? "danger" : pickup ? "brand" : "muted", act });
  });
  (d.diary || []).forEach((x, i) => {
    const m = pragueMin(x.at);
    ev.push({ key: `di-${x.project_id}-${i}`, kind: "denik", m: m ?? 0, t: x.time || "", title: "Zápis do deníku",
      sub: join(x.project_name, x.photos ? plur(x.photos, "fotka", "fotky", "fotek") : null, x.author_name),
      pay: x.finished ? "" : "rozepsáno", payTone: "brand", act: { type: "diary", projectId: x.project_id } });
  });
  (d.diaryMissing || []).forEach((x) => {
    const n = x.people || 0;
    ev.push({ key: `dm-${x.project_id}-${x.date}`, kind: "denik", up: true, old: true, m: -daysBetween(x.date, today) * 1440,
      t: dayLabel(x.date, today), title: "Zapsat deník",
      sub: join(x.project_name, n === 1 ? "byl tam 1 člověk" : n > 1 ? `byli tam ${n} ${lidi(n)}` : "jsou tam podklady"),
      pay: "chybí", payTone: "brand", act: { type: "diary", projectId: x.project_id } });
  });
  return ev;
}

// ---- dlaždice ----
function tiles(d, ev, live, meId) {
  const T = d.tasks || {};
  const lateToday = ev.filter((e) => e.kind === "ukoly" && e.late && !e.old).length;
  const nLate = (T.overdueCount || 0) + lateToday;
  const workIds = uniq(live.map((p) => p.user_id));
  const ini = uniq(live.slice().sort((a, b) => (b.user_id === meId) - (a.user_id === meId)).map((p) => p.user_id))
    .map((id) => live.find((p) => p.user_id === id)).map((p) => ({ id: p.user_id, ini: p.initials || "?", me: p.user_id === meId }));
  const over = (d.due || []).filter((x) => (x.days_overdue || 0) > 0);
  const dueToday = (d.due || []).length - over.length;
  const names = uniq(over.map((x) => x.title));
  const missing = d.diaryMissing || [];
  const diaryToday = (d.diary || []).length;
  let diary;
  if (missing.length) {
    const lbl = dayLabel(missing[0].date, d.today);
    diary = {
      title: missing.length === 1 ? (lbl === "včera" ? "Včera nezapsán" : `Nezapsán ${lbl}`) : plur(missing.length, "nezapsaný den", "nezapsané dny", "nezapsaných dnů"),
      foot: `Zapsat · ${missing[0].project_name || "deník"}`, tone: "brand",
    };
  } else if (diaryToday) {
    diary = { title: `Dnes ${plur(diaryToday, "zápis", "zápisy", "zápisů")}`, foot: "vše zapsáno", tone: "ok" };
  } else {
    diary = { title: "Vše zapsáno", foot: "dnes zatím bez zápisu", tone: "muted" };
  }
  return {
    work: { count: workIds.length, of: d.employeeCount, avatars: ini.slice(0, 3), more: Math.max(0, ini.length - 3) },
    tasks: { big: (T.today || []).length + (T.done || []).length, open: T.openCount || 0, foot: nLate ? `${nLate} po termínu` : "vše v termínu", tone: nLate ? "danger" : "ok" },
    invoices: {
      big: over.length,
      foot: over.length ? names.slice(0, 2).join(", ") + (names.length > 2 ? " a další" : "")
        : dueToday ? `${dueToday} ${dueToday === 1 ? "splatná" : "splatné"} dnes` : "nic po splatnosti",
      tone: over.length || dueToday ? "danger" : "muted",
    },
    projects: { big: d.projectsLive ?? 0 },
    diary,
  };
}

// Hlavní výstup: { ups, now, live, past, hidden, empty, tiles } — ups i past vzestupně (251)
// 330: `off` = zdroje, které si člověk na Přehledu vypnul (Můj Přehled) — zmizí z osy, filtrů i docházky
export function buildToday(data, { filter = "vse", showAll = false, nowMin = 0, meId = null, limit = 5, off = [] } = {}) {
  const d = data || {};
  const f = FILTERS.some(([k]) => k === filter && !off.includes(k)) ? filter : "vse";
  const noAtt = off.includes("dochazka");
  const ev = events(d, nowMin).filter((e) => !off.includes(e.kind));
  const ppl = (d.people || []).map((p) => ({ ...p, label: p.user_id === meId ? "Ty" : p.name }));
  const live = ppl.filter((p) => p.running).sort((a, b) => ts(a.in_at) - ts(b.in_at));
  const fin = ppl.filter((p) => !p.running).sort((a, b) => ts(b.out_at) - ts(a.out_at));
  const fromDay = (p) => (p.in_at && pragueDate(p.in_at) !== d.today ? "včera " : "");

  // 258: jen moje (dělník, „Jen moje“) — každá směna zvlášť, ne souhrn „Odchody · 1 člověk / Ty“
  const own = d.scope === "me";
  const list = ev.slice();
  if (!noAtt && (f === "dochazka" || (own && f === "vse"))) {
    fin.forEach((p) => {
      const [pay, tone] = p.auto_closed && p.approval !== "approved"
        ? (p.self_declared ? ["doplněno · čeká", "brand"] : [own ? "nebyl jsi odhlášen" : "auto odhlášeno", "danger"])
        : (APPROVAL[p.approval] || ["", "muted"]);
      list.push({ key: `p-${p.id}`, kind: "dochazka", m: pragueMin(p.out_at) ?? 0, t: p.out || "", title: own ? "Směna" : p.label,
        sub: join(p.project_name, `${fromDay(p)}${p.in || "?"}–${p.out || "?"}`), amt: decH(p.hours), pay, payTone: tone,
        act: { type: "person", row: p } });
    });
  }
  if (!noAtt && f === "vse" && fin.length && !own) {
    const n = uniq(fin.map((p) => p.user_id)).length;
    const wait = fin.filter((p) => p.approval !== "approved").length;
    list.push({ key: "p-fin", kind: "dochazka", m: pragueMin(fin[0].out_at) ?? 0, t: fin[0].out || "",
      title: `Odchody · ${n} ${lidi(n)}`, sub: uniq(fin.map((p) => first(p.label))).join(", "),
      amt: decH(fin.reduce((a, p) => a + (Number(p.hours) || 0), 0)),
      pay: wait ? `${wait} ke schválení` : "schváleno", payTone: wait ? "brand" : "ok", act: { type: "filter", f: "dochazka" } });
  }

  // stejná minuta: zachovat pořadí vložení (sort je stabilní)
  list.sort((a, b) => b.m - a.m);
  const shown = list.filter((e) => (f === "vse" ? !e.old && !e.only : e.kind === f));
  const ups = shown.filter((e) => e.up);
  const past = shown.filter((e) => !e.up);
  const lim = f === "vse" ? limit : Infinity;
  const cut = showAll ? past.length : Math.min(past.length, lim);

  let liveRows = [];
  if (noAtt) {
    liveRows = [];
  } else if (f === "dochazka" || (own && f === "vse")) {
    liveRows = live.map((p) => ({ key: `l-${p.id}`, kind: "dochazka", live: true, t: "", title: own ? "V práci" : p.label,
      sub: join(p.project_name, `od ${fromDay(p)}${p.in || "?"}`), amt: decH(p.hours), amtTone: "ok", pay: "v práci", payTone: "ok",
      act: { type: "person", row: p } }));
  } else if (f === "vse" && live.length) {
    const n = uniq(live.map((p) => p.user_id)).length;
    liveRows = [{ key: "l-all", kind: "dochazka", live: true, t: "", title: `V práci · ${n} ${lidi(n)}`,
      sub: uniq(live.map((p) => first(p.label))).join(", "), pay: "teď", payTone: "ok", act: { type: "filter", f: "dochazka" } }];
  }

  // 251: osa jde shora dolů vzestupně — nahoře co proběhlo (schované jsou nejstarší), pod „Teď“ co čeká (nejbližší první)
  const asc = (a, b) => a.m - b.m;
  return {
    filter: f,
    counts: off.includes("ukoly") ? {} : { ukoly: (d.tasks || {}).openCount || 0 },   // 328: odznak na filtru Úkoly = všechny nedokončené
    ups: ups.slice().sort(asc),
    now: minToHm(Math.floor(nowMin)),
    live: liveRows,
    past: past.slice(0, cut).sort(asc),
    hidden: past.length - cut,
    canCollapse: showAll && past.length > lim,
    empty: !past.length && !liveRows.length ? EMPTY[f] : null,
    tiles: tiles(d, ev, live, meId),
  };
}
